import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Radio, 
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Link,
  LinearProgress,
  Stack,
  MenuItem,
  Grid,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Lock,
  Email,
  Phone,
  LocationOn,
  AccountCircle,
  HowToReg,
  ArrowBack,
  Business
} from '@mui/icons-material';
import { PasswordChecker } from '../utils/PasswordChecker';
import { api } from '../api/client';
import PasswordStrengthMeter from '../components/auth/PasswordStrengthMeter';
import CountryCitySelector from '../components/auth/CountryCitySelector';
import { normalizePhone, normalizeId, phonesEqual, idsEqual, formatPhoneForDisplay } from '../utils/normalization';

// Remove the static countries array and add these interfaces
interface Country {
  code: string;
  name: string;
  cities: string[];
}

interface City {
  name: string;
  latitude: string;
  longitude: string;
}

const southernAfricanCountries: Country[] = [
  {
    code: 'ZW',
    name: 'Zimbabwe',
    cities: [
      'Harare', 'Bulawayo', 'Chitungwiza', 'Mutare', 'Gweru', 'Kwekwe', 'Kadoma', 'Masvingo', 'Chinhoyi', 'Victoria Falls',
      'Bindura', 'Beitbridge', 'Chegutu', 'Chipinge', 'Chiredzi', 'Gokwe', 'Gwanda', 'Hwange', 'Kariba', 'Karoi',
      'Lupane', 'Marondera', 'Mashava', 'Mount Darwin', 'Mvurwi', 'Norton', 'Plumtree', 'Redcliff', 'Rusape', 'Ruwa',
      'Shurugwi', 'Zvishavane', 'Epworth', 'Glendale', 'Banket', 'Mutoko', 'Nyanga', 'Triangle', 'Chimanimani', 'Gutu'
    ]
  },
  {
    code: 'ZA',
    name: 'South Africa',
    cities: [
      'Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Port Elizabeth', 'Bloemfontein', 'Nelspruit', 'Kimberley', 'Polokwane', 'East London',
      'Pietermaritzburg', 'Rustenburg', 'Potchefstroom', 'Welkom', 'Newcastle', 'Witbank', 'Krugersdorp', 'Paarl', 'Worcester', 'George',
      'Stellenbosch', 'Brakpan', 'Richards Bay', 'Vanderbijlpark', 'Grahamstown', 'Kroonstad', 'Uitenhage', 'Vereeniging', 'Benoni', 'Springs',
      'Alberton', 'Klerksdorp', 'Middelburg', 'Vryheid', 'Carletonville', 'Queenstown', 'Bhisho', 'Heidelberg', 'Secunda', 'Ermelo'
    ]
  },
  {
    code: 'BW',
    name: 'Botswana',
    cities: [
      'Gaborone', 'Francistown', 'Molepolole', 'Maun', 'Serowe', 'Selibe Phikwe', 'Kanye', 'Mahalapye', 'Palapye', 'Lobatse',
      'Mogoditshane', 'Mochudi', 'Letlhakane', 'Tonota', 'Bobonong', 'Ramotswa', 'Thamaga', 'Kasane', 'Moshupa', 'Gabane',
      'Tutume', 'Jwaneng', 'Orapa', 'Shakawe', 'Ghanzi', 'Tsabong', 'Gumare', 'Nata', 'Charleshill', 'Rakops'
    ]
  },
  {
    code: 'NA',
    name: 'Namibia',
    cities: [
      'Windhoek', 'Walvis Bay', 'Swakopmund', 'Oshakati', 'Rundu', 'Katima Mulilo', 'Otjiwarongo', 'Keetmanshoop', 'Grootfontein', 'Gobabis',
      'Mariental', 'Okahandja', 'Outjo', 'Tsumeb', 'Lüderitz', 'Omaruru', 'Rehoboth', 'Ondangwa', 'Oranjemund', 'Karasburg',
      'Opuwo', 'Outapi', 'Henties Bay', 'Karibib', 'Aranos', 'Otavi', 'Bethanie', 'Usakos', 'Oshikango', 'Khorixas'
    ]
  },
  {
    code: 'MZ',
    name: 'Mozambique',
    cities: [
      'Maputo', 'Matola', 'Beira', 'Nampula', 'Chimoio', 'Nacala', 'Quelimane', 'Tete', 'Xai-Xai', 'Pemba',
      'Lichinga', 'Inhambane', 'Maxixe', 'Angoche', 'Cuamba', 'Montepuez', 'Dondo', 'Mocuba', 'Chibuto', 'Chokwe',
      'Gurué', 'Manica', 'Moatize', 'Vilankulo', 'Mocímboa da Praia', 'Ilha de Moçambique', 'Manhiça', 'Gondola', 'Massinga', 'Marromeu'
    ]
  },
  {
    code: 'ZM',
    name: 'Zambia',
    cities: [
      'Lusaka', 'Kitwe', 'Ndola', 'Kabwe', 'Chingola', 'Mufulira', 'Livingstone', 'Luanshya', 'Chipata', 'Kasama',
      'Chililabombwe', 'Kafue', 'Solwezi', 'Mazabuka', 'Mongu', 'Kapiri Mposhi', 'Mansa', 'Monze', 'Choma', 'Mpika',
      'Kalulushi', 'Samfya', 'Petauke', 'Nchelenge', 'Lundazi', 'Mbala', 'Kaoma', 'Serenje', 'Nakonde', 'Siavonga'
    ]
  },
  {
    code: 'MW',
    name: 'Malawi',
    cities: [
      'Lilongwe', 'Blantyre', 'Mzuzu', 'Zomba', 'Kasungu', 'Mangochi', 'Karonga', 'Salima', 'Nkhotakota', 'Balaka',
      'Dedza', 'Liwonde', 'Mzimba', 'Ntcheu', 'Rumphi', 'Nsanje', 'Mulanje', 'Thyolo', 'Mponela', 'Chitipa',
      'Monkey Bay', 'Mchinji', 'Nkhata Bay', 'Dowa', 'Ntchisi', 'Chikwawa', 'Machinga', 'Chiradzulu', 'Phalombe', 'Luchenza'
    ]
  },
  {
    code: 'LS',
    name: 'Lesotho',
    cities: [
      'Maseru', 'Teyateyaneng', 'Mafeteng', 'Hlotse', 'Mohales Hoek', 'Maputsoe', 'Qacha\'s Nek', 'Quthing', 'Butha-Buthe', 'Thaba-Tseka',
      'Roma', 'Peka', 'Mokhotlong', 'Leribe', 'Morija', 'Pitseng', 'Semonkong', 'Mount Moorosi', 'Nako', 'Mantsonyane',
      'Tsoelike', 'Sehlabathebe', 'Ha Lejone', 'Ramabanta', 'Malealea', 'Mapholaneng', 'Thaba-Phatsoa', 'Marakabei', 'Ketane', 'Mpaki'
    ]
  },
  {
    code: 'SZ',
    name: 'Eswatini',
    cities: [
      'Mbabane', 'Manzini', 'Big Bend', 'Malkerns', 'Nhlangano', 'Piggs Peak', 'Siteki', 'Lobamba', 'Simunye', 'Tshaneni',
      'Ezulwini', 'Matsapha', 'Hluti', 'Bulembu', 'Ngwenya', 'Mhlume', 'Vuvulane', 'Mankayane', 'Lavumisa', 'Bhunya',
      'Kubuta', 'Sidvokodvo', 'Mpaka', 'Lomahasha', 'Nsoko', 'Siphofaneni', 'Tambankulu', 'Kwaluseni', 'Mhlambanyatsi', 'Ngomane'
    ]
  }
];

// Registration types enum
enum RegistrationType {
  EXISTING_CUSTOMER = 'existing_customer',
  NEW_USER = 'new_user'
}

interface RegistrationTypeOption {
  value: RegistrationType;
  label: string;
  description: string;
}

const registrationTypes: RegistrationTypeOption[] = [
  {
    value: RegistrationType.EXISTING_CUSTOMER,
    label: 'Existing SMEDCO Customer',
    description: 'I have an existing account with SMEDCO and want full access to the customer portal (accounts, transactions, payments, etc.)'
  },
  {
    value: RegistrationType.NEW_USER,
    label: 'New Customer',
    description: 'I am new to SMEDCO and want to create an account and apply for loans. Limited access until loan is approved and account is created.'
  }
];

// Registration steps enum
enum RegistrationStep {
  REGISTRATION_DETAILS = 'registration_details',
  EMAIL_VERIFICATION = 'email_verification',
  REGISTRATION_TYPE_SELECTION = 'registration_type_selection',
  ACCOUNT_VERIFICATION = 'account_verification',
  OTP_VERIFICATION = 'otp_verification'
}

const Register: React.FC = () => {
  const [registrationType, setRegistrationType] = useState<RegistrationType>(RegistrationType.NEW_USER);
  const [currentStep, setCurrentStep] = useState<RegistrationStep>(RegistrationStep.REGISTRATION_DETAILS);
  
  // Account verification fields
  const [accountNumber, setAccountNumber] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [companyRegistration, setCompanyRegistration] = useState('');
  const [phone, setPhone] = useState('');
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  
  // OTP verification fields
  const [otp, setOtp] = useState('');
  
  // Email verification fields
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState('');
  const [emailVerificationResendCooldown, setEmailVerificationResendCooldown] = useState(0);
  const [canResendEmailCode, setCanResendEmailCode] = useState(false);
  
  // Registration details fields
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // UI state
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [retryAllowed, setRetryAllowed] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const [cities, setCities] = useState<string[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Update cities when country changes
  useEffect(() => {
    if (country) {
      const selectedCountry = southernAfricanCountries.find(c => c.code === country);
      setCities(selectedCountry?.cities || []);
      setCity(''); // Reset city when country changes
    } else {
      setCities([]);
    }
  }, [country]);

  // Reset relevant fields when registration type changes
  useEffect(() => {
    setAccountNumber('');
    setError('');
  }, [registrationType]);

  const validateEmail = (email: string) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const validateMobileNumber = (number: string) => {
    // Remove any non-digit characters
    const cleanNumber = number.replace(/\D/g, '');
    // Check if it's exactly 10 digits
    return cleanNumber.length === 10 && /^\d+$/.test(cleanNumber);
  };

  const getPasswordStrengthColor = (strength: number) => {
    if (strength < 0.3) return '#f44336'; // Weak - Red
    if (strength < 0.6) return '#ff9800'; // Medium - Orange
    if (strength < 0.8) return '#2196f3'; // Strong - Blue
    return '#4caf50'; // Very Strong - Green
  };

  const getPasswordStrengthText = (strength: number) => {
    if (strength < 0.3) return 'Weak';
    if (strength < 0.6) return 'Medium';
    if (strength < 0.8) return 'Strong';
    return 'Very Strong';
  };

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    setPasswordStrength(PasswordChecker.calculateStrength(newPassword));
  };

  const validateAccountVerification = () => {
    if (registrationType === RegistrationType.EXISTING_CUSTOMER) {
      // Trim string inputs for validation
      const trimmedAccountNumber = accountNumber?.trim() || '';
      const trimmedCompanyRegistration = companyRegistration?.trim() || '';
      
      if (!trimmedAccountNumber) {
        setError('Please enter your account number');
        return false;
      }
      if (!trimmedCompanyRegistration) {
        setError('Please enter your Company Registration number');
        return false;
      }
      // Phone number is already validated in registration details step
    }
    return true;
  };

  const validateOtpVerification = () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return false;
    }
    return true;
  };

  const validateRegistrationDetails = () => {
    // Trim all string inputs for validation
    const trimmedUsername = username?.trim() || '';
    const trimmedFirstName = firstName?.trim() || '';
    const trimmedLastName = lastName?.trim() || '';
    const trimmedCountry = country?.trim() || '';
    const trimmedCity = city?.trim() || '';
    const trimmedEmail = email?.trim() || '';
    const trimmedPhoneNumber = phoneNumber?.trim() || '';
    const trimmedNationalId = nationalId?.trim() || '';

    if (!trimmedUsername || trimmedUsername.length < 6) {
      setError('Username should be greater than 6 characters');
      return false;
    }
    if (!trimmedFirstName) {
      setError('Please enter your first name');
      return false;
    }
    if (!trimmedLastName) {
      setError('Please enter your last name');
      return false;
    }
    if (!trimmedCountry) {
      setError('Please enter your country');
      return false;
    }
    if (!trimmedCity) {
      setError('Please enter your city');
      return false;
    }
    if (!trimmedEmail) {
      setError('Please enter your email');
      return false;
    }
    if (!validateEmail(trimmedEmail)) {
      setError('Invalid email address');
      return false;
    }
    if (!trimmedPhoneNumber) {
      setError('Please enter your phone number');
      return false;
    }
    if (!validateMobileNumber(trimmedPhoneNumber)) {
      setError('Phone number must be 10 digits long');
      return false;
    }
    if (!trimmedNationalId) {
      setError('Please enter your National ID');
      return false;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (passwordStrength < 0.6) {
      setError('Please choose a stronger password');
      return false;
    }
    return true;
  };

  const handleAccountVerification = async () => {
    if (!validateAccountVerification()) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      
      
      const startBody = {
        accountNumber: accountNumber?.trim(),
        nationalId: nationalId?.trim(),
        companyRegistration: companyRegistration?.trim() || null,
        phone: phoneNumber?.trim(), // Use the phone from registration details
        username: username?.trim() // Pass username to look up keycloakUserId
      };
      
      const startRes = await api.post('/api/auth/register/start', startBody);
      setTransactionId(startRes.data?.transactionId || null);
      
      if (!startRes.data?.transactionId) {
        throw new Error('Failed to start verification');
      }
      
      setMaskedPhone(formatPhoneForDisplay(phoneNumber));
      setSuccess(`Verification code sent to ${formatPhoneForDisplay(phoneNumber)}. Please enter the OTP.`);
      setCurrentStep(RegistrationStep.OTP_VERIFICATION);
      
    } catch (err: any) {
      const response = err.response?.data;
      const msg = response?.message || 'Verification failed. Please check your details and try again.';
      const details = response?.details || '';
      const retry = response?.retryAllowed || false;
      const attempts = response?.remainingAttempts || 0;
      
      setError(msg);
      setErrorDetails(details);
      setRetryAllowed(retry);
      setRemainingAttempts(attempts);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    if (!validateOtpVerification()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const verifyRes = await api.post('/api/auth/register/verify-otp', { transactionId, otp });
      
      if (verifyRes.data?.status !== 'verified') {
        throw new Error('Invalid code');
      }
      
      setSuccess('Account verified successfully! You can now sign in.');
      setTimeout(() => { navigate('/login'); }, 2000);
      
    } catch (err: any) {
      const response = err.response?.data;
      const msg = response?.message || 'OTP verification failed.';
      const details = response?.details || '';
      const retry = response?.retryAllowed || false;
      const attempts = response?.remainingAttempts || 0;
      
      setError(msg);
      setErrorDetails(details);
      setRetryAllowed(retry);
      setRemainingAttempts(attempts);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setErrorDetails('');
    setRetryAllowed(false);
    setRemainingAttempts(0);
    setOtp('');
    // Reset to the appropriate step based on current step
    if (currentStep === RegistrationStep.OTP_VERIFICATION) {
      setCurrentStep(RegistrationStep.ACCOUNT_VERIFICATION);
    }
  };

  const handleResendOtp = async () => {
    if (!transactionId) {
      setError('No verification session found. Please restart the process.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/auth/register/resend-otp', {
        transactionId
      });

      if (response.status === 200) {
        setSuccess(response.data.message || 'New verification code sent successfully!');
        setCanResend(false);
        setResendCooldown(60); // 60 seconds cooldown
      } else {
        setError(response.data.message || 'Failed to resend verification code');
      }
    } catch (err: any) {
      const response = err.response?.data;
      const msg = response?.message || 'Failed to resend verification code. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Countdown timer effect for OTP resend
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendCooldown]);
  
  // Countdown timer effect for email verification code resend
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (emailVerificationResendCooldown > 0) {
      interval = setInterval(() => {
        setEmailVerificationResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResendEmailCode(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [emailVerificationResendCooldown]);

  // Enable resend after OTP is sent initially
  useEffect(() => {
    if (currentStep === RegistrationStep.OTP_VERIFICATION && transactionId) {
      setCanResend(true);
      setResendCooldown(0);
    }
  }, [currentStep, transactionId]);
  
  // Handle email verification
  const handleEmailVerification = async () => {
    if (!emailVerificationCode || emailVerificationCode.length !== 6) {
      setEmailVerificationError('Please enter the 6-digit verification code');
      return;
    }
    
    setEmailVerificationLoading(true);
    setEmailVerificationError('');
    
    try {
      const response = await api.post('/api/auth/email-verification/verify', {
        code: emailVerificationCode,
        email: email
      });
      
      if (response.status === 200) {
        setSuccess('Email verified successfully! Please select your account type.');
        setCurrentStep(RegistrationStep.REGISTRATION_TYPE_SELECTION);
        setEmailVerificationCode('');
      } else {
        setEmailVerificationError(response.data?.message || 'Verification failed. Please try again.');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || 
                          errorData?.error || 
                          'Failed to verify email. Please check the code and try again.';
      setEmailVerificationError(errorMessage);
      
      // Show remaining attempts if available
      if (errorData?.remainingAttempts !== undefined) {
        setRemainingAttempts(errorData.remainingAttempts);
      }
    } finally {
      setEmailVerificationLoading(false);
    }
  };
  
  // Handle resend email verification code
  const handleResendEmailCode = async () => {
    setEmailVerificationLoading(true);
    setEmailVerificationError('');
    
    try {
      const response = await api.post('/api/auth/email-verification/send', {
        usernameOrEmail: email
      });
      
      if (response.status === 200) {
        setSuccess('Verification code sent successfully! Please check your email.');
        setCanResendEmailCode(false);
        setEmailVerificationResendCooldown(60); // 60 seconds cooldown
      } else {
        setEmailVerificationError('Failed to resend verification code. Please try again.');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      setEmailVerificationError(errorData?.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const handleRegistrationDetails = async () => {
    if (!validateRegistrationDetails()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const registrationData = {
        username: username?.trim(),
        password,
        email: email?.trim(),
        firstName: firstName?.trim(),
        lastName: lastName?.trim(),
        country: country?.trim(),
        city: city?.trim(),
        phoneNumber: phoneNumber?.trim(),
        nationalId: nationalId?.trim(),
        userType: registrationType
        // Note: accountNumber and companyRegistration are only sent during account verification step
      };


      const response = await api.post('/api/auth/register', registrationData);
      const data = response.data;
      
      if (response.status === 200 && data && data.message && data.message.includes('User registered successfully')) {
        // Always go through email verification after registration
        // Even if backend does not send emailVerificationRequired flag
        setSuccess('Account created successfully! Please check your email for the verification code.');
        setEmail(email?.trim() || '');
        setCurrentStep(RegistrationStep.EMAIL_VERIFICATION);
        setCanResendEmailCode(true);
        setEmailVerificationResendCooldown(0);
      } else {
        setError(data.error_description || data.message || 'Registration failed. Please try again.');
      }
    } catch (err: any) {
      // Handle structured error responses from backend
      const errorData = err.response?.data;
      let errorMessage = 'Registration failed. Please try again.';
      
      if (errorData) {
        // Prefer the 'message' field which contains user-friendly messages
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error_description) {
          errorMessage = errorData.error_description;
        } else if (errorData.error) {
          // If only error code is present, provide a generic message
          errorMessage = 'Registration failed. Please check your information and try again.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentStep === RegistrationStep.REGISTRATION_DETAILS) {
      // Save registration details to Keycloak first
      await handleRegistrationDetails();
    } else if (currentStep === RegistrationStep.EMAIL_VERIFICATION) {
      // Verify email with code
      await handleEmailVerification();
    } else if (currentStep === RegistrationStep.REGISTRATION_TYPE_SELECTION) {
      // After saving to Keycloak, ask for account type
      if (registrationType === RegistrationType.EXISTING_CUSTOMER) {
        setCurrentStep(RegistrationStep.ACCOUNT_VERIFICATION);
      } else {
        // New user - registration complete
        setSuccess('Registration completed successfully! You can now log in with limited access.');
        setTimeout(() => { navigate('/login'); }, 3000);
      }
    } else if (currentStep === RegistrationStep.ACCOUNT_VERIFICATION) {
      await handleAccountVerification();
    } else if (currentStep === RegistrationStep.OTP_VERIFICATION) {
      await handleOtpVerification();
    }
  };

  return (
          <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
      <Container maxWidth="md">
        <Paper 
          elevation={24} 
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* SMEDCO Logo and Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box 
              component="img"
              src="/logo.png"
              alt="SMEDCO Logo"
              sx={{ 
                height: 80,
                width: 'auto',
                mb: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
              }}
            />
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Join SMEDCO
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Create your account and start your financial journey
            </Typography>
          </Box>

          {/* Alerts */}
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { fontSize: '1.5rem' }
              }}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { fontSize: '1.5rem' }
              }}
              action={
                retryAllowed && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleRetry}
                    sx={{ 
                      fontWeight: 'bold',
                      textTransform: 'none'
                    }}
                  >
                    Try Again
                  </Button>
                )
              }
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: errorDetails ? 1 : 0 }}>
                  {error}
                </Typography>
                {errorDetails && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {errorDetails}
                  </Typography>
                )}
                {remainingAttempts > 0 && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {remainingAttempts} attempt(s) remaining
                  </Typography>
                )}
              </Box>
            </Alert>
          )}

          {/* Step Indicator */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              {[
                { step: RegistrationStep.REGISTRATION_DETAILS, label: 'Registration Details', icon: <HowToReg /> },
                { step: RegistrationStep.EMAIL_VERIFICATION, label: 'Verify Email', icon: <Email /> },
                { step: RegistrationStep.REGISTRATION_TYPE_SELECTION, label: 'Account Type', icon: <AccountCircle /> },
                { step: RegistrationStep.ACCOUNT_VERIFICATION, label: 'Verify Account', icon: <Phone /> },
                { step: RegistrationStep.OTP_VERIFICATION, label: 'Enter OTP', icon: <Phone /> }
              ].filter(item => {
                // Only show email verification step if it's the current step or we've passed it
                if (item.step === RegistrationStep.EMAIL_VERIFICATION) {
                  return currentStep === RegistrationStep.EMAIL_VERIFICATION || 
                         Object.values(RegistrationStep).indexOf(currentStep) > Object.values(RegistrationStep).indexOf(RegistrationStep.EMAIL_VERIFICATION);
                }
                return true;
              }).map((item, index) => (
                <Box key={item.step} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: currentStep === item.step ? 'primary.main' : 
                                     (Object.values(RegistrationStep).indexOf(currentStep) > index ? 'success.main' : 'grey.300'),
                      color: 'white',
                      mr: 1
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="body2" sx={{ mr: 2, fontWeight: currentStep === item.step ? 600 : 400 }}>
                    {item.label}
                  </Typography>
                  {index < 3 && <Box sx={{ width: 20, height: 2, backgroundColor: 'grey.300', mr: 2 }} />}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Registration Form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Step 1: Registration Details Only */}
              {currentStep === RegistrationStep.REGISTRATION_DETAILS && (
                <>
                  {/* Registration Details Fields */}
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      label="Username" 
                      fullWidth 
                      value={username} 
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      helperText="At least 6 characters"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      label="First Name" 
                      fullWidth 
                      value={firstName} 
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      label="Last Name" 
                      fullWidth 
                      value={lastName} 
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      label="Email" 
                      fullWidth 
                      type="email"
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="National ID"
                      value={nationalId}
                      onChange={(e) => setNationalId(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      size="medium"
                      helperText="Your National ID number (required for all customers)"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HowToReg sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      label="Phone Number" 
                      fullWidth 
                      value={phoneNumber} 
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      helperText="10 digits required"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <CountryCitySelector
                    country={country}
                    setCountry={setCountry}
                    city={city}
                    setCity={setCity}
                    countries={southernAfricanCountries}
                    cities={cities}
                  />
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      error={!!error && (!password || password.length < 8)}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: 'text.secondary' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      fullWidth
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      error={!!error && password !== confirmPassword}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              sx={{ color: 'text.secondary' }}
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  {password && (
                    <Grid item xs={12}>
                      <PasswordStrengthMeter
                        strength={passwordStrength}
                        getColor={getPasswordStrengthColor}
                        getText={getPasswordStrengthText}
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Step 2: Email Verification */}
              {currentStep === RegistrationStep.EMAIL_VERIFICATION && (
                <Grid item xs={12}>
                  <Card sx={{ p: 4, textAlign: 'center' }}>
                    <Email sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Verify Your Email Address
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      We've sent a 6-digit verification code to <strong>{email}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Please check your email and enter the code below to verify your email address.
                    </Typography>
                    
                    {emailVerificationError && (
                      <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                        {emailVerificationError}
                        {remainingAttempts > 0 && (
                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            {remainingAttempts} attempt(s) remaining
                          </Typography>
                        )}
                      </Alert>
                    )}
                    
                    <TextField
                      label="Enter Verification Code"
                      value={emailVerificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setEmailVerificationCode(value);
                        setEmailVerificationError('');
                      }}
                      fullWidth
                      required
                      placeholder="000000"
                      inputProps={{ 
                        maxLength: 6, 
                        pattern: "[0-9]*",
                        style: { 
                          textAlign: 'center', 
                          fontSize: '1.5rem',
                          letterSpacing: '0.5rem',
                          fontFamily: 'monospace'
                        }
                      }}
                      sx={{ maxWidth: 300, mx: 'auto', mb: 3 }}
                      error={!!emailVerificationError}
                    />
                    
                    {/* Resend Email Code Section */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Didn't receive the code?
                      </Typography>
                      
                      {canResendEmailCode ? (
                        <Button
                          onClick={handleResendEmailCode}
                          variant="outlined"
                          size="medium"
                          disabled={emailVerificationLoading}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              borderColor: 'primary.dark',
                              backgroundColor: 'primary.50',
                            },
                          }}
                        >
                          {emailVerificationLoading ? 'Sending...' : 'Resend Code'}
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Resend available in:
                          </Typography>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: 'primary.50',
                              border: '1px solid',
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }}
                          >
                            {emailVerificationResendCooldown}s
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              )}

              {/* Step 3: Registration Type Selection */}
              {currentStep === RegistrationStep.REGISTRATION_TYPE_SELECTION && (
                <>
                  <Grid item xs={12}>
                    <Card sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Account Created Successfully!
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Your account has been created. Please select your account type to continue.
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Card sx={{ p: 3, mb: 2, background: 'rgba(102, 126, 234, 0.05)' }}>
                      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                          Select Your Account Type
                        </Typography>
                        <RadioGroup
                          value={registrationType}
                          onChange={(e) => setRegistrationType(e.target.value as RegistrationType)}
                        >
                          {registrationTypes.map((type) => (
                            <Box key={type.value} sx={{ mb: 2 }}>
                              <FormControlLabel
                                value={type.value}
                                control={<Radio color="primary" />}
                                label={
                                  <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                      {type.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {type.description}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Box>
                          ))}
                        </RadioGroup>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}

              {/* Step 3: Account Verification (only for existing customers) */}
              {currentStep === RegistrationStep.ACCOUNT_VERIFICATION && (
                <>
                  <Grid item xs={12}>
                    <Card sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Verify Your SMEDCO Account
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        To get full access to your SMEDCO account, please verify your account details below.
                      </Typography>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField 
                      label="SMEDCO Account Number" 
                      fullWidth 
                      value={accountNumber} 
                      onChange={(e) => setAccountNumber(e.target.value)}
                      required
                      variant="outlined"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountCircle sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Company Registration Number"
                      value={companyRegistration}
                      onChange={(e) => setCompanyRegistration(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      size="medium"
                      helperText="Your company registration number (required for verification)"
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Step 4: OTP Verification */}
              {currentStep === RegistrationStep.OTP_VERIFICATION && (
                <Grid item xs={12}>
                  <Card sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Verify Your Account
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      We've sent a 6-digit verification code to {maskedPhone}
                    </Typography>
                    <TextField
                      label="Enter Verification Code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      fullWidth
                      required
                      placeholder="Enter 6-digit code"
                      inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
                      sx={{ maxWidth: 300, mx: 'auto' }}
                    />
                    
                    {/* Resend OTP Section */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Didn't receive the code?
                      </Typography>
                      
                      {canResend ? (
                        <Button
                          onClick={handleResendOtp}
                          variant="outlined"
                          size="medium"
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              borderColor: 'primary.dark',
                              backgroundColor: 'primary.50',
                            },
                          }}
                        >
                          {loading ? 'Sending...' : 'Resend Code'}
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Resend available in:
                          </Typography>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: 'primary.50',
                              border: '1px solid',
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }}
                          >
                            {resendCooldown}s
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              )}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth 
                  size="large"
                  disabled={loading || emailVerificationLoading}
                  startIcon={<HowToReg />}
                  sx={{
                    py: 1.5,
                    mt: 3,
                    mb: 2,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #152a5a 0%, #1e3c72 100%)',
                      boxShadow: '0 12px 35px rgba(30, 60, 114, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #8fa3c7 0%, #9bb3d1 100%)',
                      transform: 'none',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading || emailVerificationLoading ? 'Processing...' : 
                   currentStep === RegistrationStep.REGISTRATION_DETAILS ? 'Create Account' :
                   currentStep === RegistrationStep.EMAIL_VERIFICATION ? 'Verify Email' :
                   currentStep === RegistrationStep.REGISTRATION_TYPE_SELECTION ? 'Continue' :
                   currentStep === RegistrationStep.ACCOUNT_VERIFICATION ? 'Verify Account' :
                   currentStep === RegistrationStep.OTP_VERIFICATION ? 'Verify OTP' :
                   'Continue'}
                </Button>
              </Grid>

              {/* Back Button for Email Verification, Registration Type Selection, Account Verification and OTP steps */}
              {(currentStep === RegistrationStep.EMAIL_VERIFICATION || currentStep === RegistrationStep.REGISTRATION_TYPE_SELECTION || currentStep === RegistrationStep.ACCOUNT_VERIFICATION || currentStep === RegistrationStep.OTP_VERIFICATION) && (
                <Grid item xs={12}>
                  <Button
                    onClick={() => {
                      if (currentStep === RegistrationStep.EMAIL_VERIFICATION) {
                        setCurrentStep(RegistrationStep.REGISTRATION_DETAILS);
                      } else if (currentStep === RegistrationStep.REGISTRATION_TYPE_SELECTION) {
                        setCurrentStep(RegistrationStep.EMAIL_VERIFICATION);
                      } else if (currentStep === RegistrationStep.ACCOUNT_VERIFICATION) {
                        setCurrentStep(RegistrationStep.REGISTRATION_TYPE_SELECTION);
                      } else if (currentStep === RegistrationStep.OTP_VERIFICATION) {
                        setCurrentStep(RegistrationStep.ACCOUNT_VERIFICATION);
                      }
                    }}
                    variant="outlined"
                    size="large"
                    fullWidth
                    startIcon={<ArrowBack />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'primary.50',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Back
                  </Button>
                </Grid>
              )}

              {/* Login Link */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="text"
                    size="large"
                    startIcon={<ArrowBack />}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.50',
                      },
                    }}
                  >
                    Already have an account? Sign in
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            © 2024 SMEDCO. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
    </Box>
  );
};

export default Register; 

